import * as THREE from 'three/webgpu';
import { Subject, type Observable } from 'rxjs';
import type { Viewer } from '../../viewer/Viewer';
import { ViewerMode } from '../../viewer/ViewerMode';
import { MeasureCalloutManager } from '../annotation/MeasureCalloutManager';
import { resolveMeasureEntityRef, isMeasurableObject } from '../selection/MeasureHitResolver';
import { bootstrapMeasureSelectionFromUnderlying } from '../selection/MeasureSelectionBootstrap';
import { MeasureProgrammaticSelectionApplier } from '../selection/MeasureProgrammaticSelectionApplier';
import { MeasurePartMeasurableGeometryExpander } from '../selection/MeasurePartMeasurableGeometryExpander';
import { MeasurePickSelectionPolicy } from '../selection/MeasurePickSelectionPolicy';
import type { MeasureTreePartSelectionDescriptor } from '../types/measure-tree-part-selection-types';
import type { MeasureTreeNodeVisualSelectionDescriptor } from '../types/measure-tree-node-visual-selection-types';
import type { MeasureDocumentContext, MeasureEntityRef } from '../types/measure-types';
import { cloneMeasureEntityRef } from '../types/measure-types';
import type { GlbDatumSlotTargetDescriptor } from '../../imported-model/datum-slot';
import { MeasureUnboundedGeometryResolver } from '../unbounded/MeasureUnboundedGeometryResolver';
import type { MeasureSessionState } from '../types/measure-session-types';
import type {
    MeasureSelectionUpdateSource,
    MeasureSelectionUpdatedPayload,
} from '../types/measure-selection-event-types';
import {
    createDefaultMeasureDisplayPolicy,
    MeasureLineVisibilityGroup,
    type MeasureDisplayPolicy,
} from '../types/measure-callout-types';
import {
    createDefaultMeasureReferencePolicy,
    MeasurePairReferenceMode,
    resolveArcCircleModeFromToolbarCommand,
    type MeasureReferencePolicy,
} from '../types/measure-reference-policy-types';
import { resolveTopologySelectionPick, resolveBRepGeometryDescriptor } from '../../utils/feature';
import {
    expandImportedModelRenderableTargets,
    ImportedAssemblyPartTargetResolver,
} from '../../imported-model/highlight/ImportedAssemblyPartTargetResolver';

/** 进入测量前不允许启动的 Viewer 模式集合。 */
const MEASURE_BLOCKED_MODES = new Set<ViewerMode>([
    // 经查询solidworks暂无不允许进入测量模式的情况
]);

/**
 * 测量命令控制器。
 *
 * 职责：
 * 1. 管理测量 Inspection 叠加会话（start/stop/pause/resume）；
 * 2. 维护已选测量实体列表，并支持重复点击 toggle；
 * 3. 通过独立测量选中通道驱动绿色叠加外观，不污染底层命令选中集。
 */
export class MeasureController {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 测量会话是否激活。 */
    private isActive: boolean = false;

    /** 是否接管指针拾取。 */
    private pickEnabled: boolean = false;

    /** 进入测量时保存的底层 Viewer 模式。 */
    private baseViewerMode: ViewerMode | null = null;

    /** 当前文档上下文。 */
    private documentContext: MeasureDocumentContext = 'part';

    /** 当前已选测量实体。 */
    private selectedEntities: MeasureEntityRef[] = [];

    /**
     * 测量点击历史（按点击顺序追加）。
     * 用于解析“当前活动锚点”：从最近一次点击向前回溯，取仍留在选中集内的实体。
     */
    private pickHistory: MeasureEntityRef[] = [];

    /** 测量标注管理器（锚点 / 后续 callout）。 */
    private readonly calloutManager: MeasureCalloutManager;

    /** 双选参考点策略（单选锚点通道与此解耦）。 */
    private referencePolicy: MeasureReferencePolicy = createDefaultMeasureReferencePolicy();

    /** 点到点模式进入前的双选参考点策略快照。 */
    private pointToPointPreviousReferencePolicy: MeasureReferencePolicy | null = null;

    /** 双选距离显示策略（Show XYZ Measurements）。 */
    private displayPolicy: MeasureDisplayPolicy = createDefaultMeasureDisplayPolicy();

    /** 拾取后选中集变更策略。 */
    private readonly pickSelectionPolicy: MeasurePickSelectionPolicy;

    /** 程序化测量选中应用器。 */
    private readonly programmaticSelectionApplier: MeasureProgrammaticSelectionApplier;

    /** 设计树零件子树可测量几何展开器。 */
    private readonly partMeasurableGeometryExpander: MeasurePartMeasurableGeometryExpander;

    /** imported-model 轻量高亮目标解析器。 */
    private readonly importedTreeNodeTargetResolver: ImportedAssemblyPartTargetResolver;

    /** 无边界测量几何解析器。 */
    private readonly unboundedGeometryResolver: MeasureUnboundedGeometryResolver;

    /** 当前测量视觉是否由设计树节点轻量选择持有。 */
    private hasTreeNodeVisualSelection = false;

    /** 测量选中集变更事件流。 */
    private readonly selectionUpdatedSubject = new Subject<MeasureSelectionUpdatedPayload>();

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.calloutManager = new MeasureCalloutManager(viewer);
        this.pickSelectionPolicy = new MeasurePickSelectionPolicy();
        this.programmaticSelectionApplier = new MeasureProgrammaticSelectionApplier();
        this.partMeasurableGeometryExpander = new MeasurePartMeasurableGeometryExpander();
        this.importedTreeNodeTargetResolver = new ImportedAssemblyPartTargetResolver();
        this.unboundedGeometryResolver = new MeasureUnboundedGeometryResolver(viewer);
    }

    /**
     * 订阅测量选中集变更事件。
     *
     * @returns 测量选中集变更 Observable。
     */
    public observeSelectionUpdated(): Observable<MeasureSelectionUpdatedPayload> {
        return this.selectionUpdatedSubject.asObservable();
    }

    /**
     * 获取测量标注管理器。
     *
     * @returns 标注管理器实例。
     */
    public getCalloutManager(): MeasureCalloutManager {
        return this.calloutManager;
    }

    /**
     * 测量会话是否激活。
     *
     * @returns 是否处于测量叠加会话。
     */
    public isMeasureActive(): boolean {
        return this.isActive;
    }

    /**
     * 是否接管指针拾取。
     *
     * @returns 测量拾取是否启用。
     */
    public isPickEnabled(): boolean {
        return this.isActive && this.pickEnabled;
    }

    /**
     * 获取测量会话底层 Viewer 模式。
     *
     * @returns 底层模式；会话未激活时返回当前 Viewer 模式。
     */
    public getBaseViewerMode(): ViewerMode {
        return this.baseViewerMode ?? this.viewer.getMode();
    }

    /**
     * 获取测量会话状态快照。
     *
     * @returns 会话状态；未激活时返回 null。
     */
    public getSessionState(): MeasureSessionState | null {
        if (!this.isActive || this.baseViewerMode === null) {
            return null;
        }

        return {
            active: this.isActive,
            pickEnabled: this.pickEnabled,
            baseViewerMode: this.baseViewerMode,
            documentContext: this.documentContext,
        };
    }

    /**
     * 当前文档上下文。
     *
     * @returns 零件或装配体上下文。
     */
    public getDocumentContext(): MeasureDocumentContext {
        return this.documentContext;
    }

    /**
     * 获取当前已选测量实体快照。
     *
     * @returns 已选实体列表副本。
     */
    public getSelectedEntities(): readonly MeasureEntityRef[] {
        return [...this.selectedEntities];
    }

    /**
     * 获取当前活动锚点实体。
     *
     * @returns 活动锚点；选中集为空时返回 null。
     */
    public getActiveAnchorEntity(): MeasureEntityRef | null {
        return this.resolveActiveAnchorEntity();
    }

    /**
     * 获取当前双选参考点策略快照。
     *
     * @returns 策略副本。
     */
    public getReferencePolicy(): MeasureReferencePolicy {
        return {
            pairMode: this.referencePolicy.pairMode,
            arcCircleMode: this.referencePolicy.arcCircleMode,
        };
    }

    /**
     * 获取当前双选距离显示策略快照。
     *
     * @returns 显示策略副本。
     */
    public getDisplayPolicy(): MeasureDisplayPolicy {
        return {
            showDxDyDzMeasurement: this.displayPolicy.showDxDyDzMeasurement,
        };
    }

    /**
     * 是否已开启 ΔX / ΔY / ΔZ 分量结果显示。
     *
     * @returns 是否在结果区列出分量。
     */
    public isDxDyDzMeasurementEnabled(): boolean {
        return this.displayPolicy.showDxDyDzMeasurement;
    }

    /**
     * 获取当前测量线可视化选中组。
     *
     * @returns 选中组。
     */
    public getLineVisibilityGroup(): MeasureLineVisibilityGroup {
        return this.calloutManager.getLineVisibilityGroup();
    }

    /**
     * 设置测量线可视化选中组（点选结果项时调用）。
     *
     * @param group 目标选中组。
     * @param options.toggleSame 为 true 且与当前组相同时取消选中。
     */
    public setLineVisibilityGroup(
        group: MeasureLineVisibilityGroup,
        options: { toggleSame?: boolean } = {},
    ): void {
        if (!this.isActive) {
            return;
        }
        this.calloutManager.setLineVisibilityGroup(group, options);
    }

    /**
     * 设置结果项 hover 预览测量线（不改变点选钉住态）。
     *
     * @param group 预览组；null 清除预览并恢复钉住组。
     */
    public setLineVisibilityHoverPreview(group: MeasureLineVisibilityGroup | null): void {
        if (!this.isActive) {
            return;
        }
        this.calloutManager.setLineVisibilityHoverPreview(group);
    }

    /**
     * 暂停测量拾取（SolidWorks 右键菜单 Select 语义）。
     *
     * 说明：不得在右键按下时自动调用——右键用于视口旋转，误触发会导致测量拾取永久切回底层选择。
     */
    public pausePick(): void {
        if (!this.isActive) {
            return;
        }
        this.pickEnabled = false;
    }

    /**
     * 恢复测量拾取（点击 Measure 对话框时触发）。
     */
    public resumePick(): void {
        if (!this.isActive) {
            return;
        }
        this.pickEnabled = true;
    }

    /**
     * 应用测量工具栏命令并刷新双选测量（不影响单选锚点）。
     *
     * @param command 工具栏命令键（与 web-client MeasureDialog 一致）。
     */
    public applyToolbarCommand(command: string): void {
        if (!this.isActive) {
            return;
        }

        const arcCircleMode = resolveArcCircleModeFromToolbarCommand(command);
        if (arcCircleMode !== null) {
            this.pointToPointPreviousReferencePolicy = null;
            this.referencePolicy = {
                pairMode: MeasurePairReferenceMode.ArcCircle,
                arcCircleMode,
            };
            this.syncCalloutForSelection();
            return;
        }

        if (command === 'point-to-point') {
            if (this.referencePolicy.pairMode === MeasurePairReferenceMode.PointToPoint) {
                this.referencePolicy = this.pointToPointPreviousReferencePolicy
                    ? this.cloneReferencePolicy(this.pointToPointPreviousReferencePolicy)
                    : createDefaultMeasureReferencePolicy();
                this.pointToPointPreviousReferencePolicy = null;
            } else {
                this.pointToPointPreviousReferencePolicy = this.cloneReferencePolicy(this.referencePolicy);
                this.referencePolicy = {
                    ...createDefaultMeasureReferencePolicy(),
                    pairMode: MeasurePairReferenceMode.PointToPoint,
                };
            }
            this.syncCalloutForSelection();
            return;
        }

        if (command === 'display-xyz') {
            this.displayPolicy = {
                showDxDyDzMeasurement: !this.displayPolicy.showDxDyDzMeasurement,
            };
            this.calloutManager.applyDisplayPolicy(this.displayPolicy);
        }
    }

    /**
     * 启动测量叠加会话。
     *
     * 设计说明：不切换底层 ViewerMode，仅激活 Inspection 会话层。
     *
     * @returns 是否成功启动。
     */
    public start(): boolean {
        if (this.isActive) {
            return true;
        }

        const currentMode = this.viewer.getMode();
        if (MEASURE_BLOCKED_MODES.has(currentMode)) {
            this.viewer.drawingNoticeCallback?.('当前模式不支持测量，请先退出正在执行的命令', 'warning');
            return false;
        }

        this.baseViewerMode = currentMode;
        this.documentContext = this.resolveDocumentContext(currentMode);

        const underlyingPicks = this.viewer.entitySelectionManager?.getSelectedPicks() ?? [];
        const bootstrapped = bootstrapMeasureSelectionFromUnderlying(underlyingPicks);

        this.selectedEntities = bootstrapped.entities;
        this.pickHistory = bootstrapped.pickHistory;
        this.referencePolicy = createDefaultMeasureReferencePolicy();
        this.pointToPointPreviousReferencePolicy = null;
        this.displayPolicy = createDefaultMeasureDisplayPolicy();
        this.isActive = true;
        this.pickEnabled = true;

        /**
         * 进入测量后清空底层命令选中，避免测量会话期间残留蓝色选中语义。
         * 引导已完成，底层选中仅作为初始输入，不应与测量绿选中并存。
         */
        this.viewer.entitySelectionManager?.clearSelectionProgrammatically({ scope: 'command' });

        this.calloutManager.applyDisplayPolicy(this.displayPolicy);
        this.calloutManager.onSessionStart(this.referencePolicy, this.pickHistory);
        this.commitSelectionChange('bootstrap');
        return true;
    }

    /**
     * 停止测量叠加会话。
     *
     * 设计说明：
     * 1. 底层 ViewerMode 保持不变，仅清理测量选中与标注叠加层；
     * 2. 必须在测量态仍有效时先解析并写回底层选中，避免 imported 拓扑代理被提前回收后按 ID 解析失败。
     */
    public stop(): void {
        if (!this.isActive && this.baseViewerMode === null) {
            return;
        }

        const finalSelectedObjects = this.resolveSelectedObjectsForWriteback();

        this.selectedEntities = [];
        this.pickHistory = [];
        this.referencePolicy = createDefaultMeasureReferencePolicy();
        this.pointToPointPreviousReferencePolicy = null;
        this.displayPolicy = createDefaultMeasureDisplayPolicy();
        this.calloutManager.onSessionStop();

        this.syncDatumSlotVisuals(null);

        if (finalSelectedObjects.length > 0) {
            this.viewer.entitySelectionManager?.selectEntitiesProgrammatically(finalSelectedObjects);
        }

        this.isActive = false;
        this.pickEnabled = false;
        this.baseViewerMode = null;

        this.viewer.interactionStateManager?.clearMeasureSelectedObjects();
        // 会话收尾只提交最终视觉状态，由统一帧调度器合并各子模块产生的脏请求。
        this.viewer.requestRender?.();
        this.publishSelectionUpdated('clear');
    }

    /**
     * 应用程序化测量选中命中。
     *
     * @param picks 程序化命中列表。
     * @param source 变更来源。
     * @returns 是否至少成功接纳一个实体。
     */
    public applyProgrammaticPicks(
        picks: readonly THREE.Intersection<THREE.Object3D>[],
        source: MeasureSelectionUpdateSource = 'programmatic',
    ): boolean {
        if (!this.isActive || picks.length <= 0) {
            return false;
        }

        const applied = this.programmaticSelectionApplier.applyPicks(
            this.selectedEntities,
            this.pickHistory,
            picks,
            this.referencePolicy,
            this.pickSelectionPolicy,
        );

        if (!applied.accepted) {
            return false;
        }

        this.selectedEntities = applied.entities;
        this.pickHistory = applied.pickHistory;
        this.commitSelectionChange(source);
        return true;
    }

    /**
     * 一次性替换测量选中集（设计树零件子树展开等批量场景）。
     *
     * 设计说明：
     * 1. 绕过 `MeasurePickSelectionPolicy` 的逐条追加/滑动替换，避免 N>2 时无法保留全集；
     * 2. pickHistory 与选中集保持一致，activeAnchor 取最后一项。
     *
     * @param entities 下一帧完整选中集。
     * @param source 变更来源。
     * @returns 是否成功写入。
     */
    public replaceSelectedEntities(
        entities: readonly MeasureEntityRef[],
        source: MeasureSelectionUpdateSource = 'feature-tree-part',
    ): boolean {
        if (!this.isActive || entities.length <= 0) {
            return false;
        }

        this.selectedEntities = entities.map((entity) => cloneMeasureEntityRef(entity));
        this.pickHistory = entities.map((entity) => cloneMeasureEntityRef(entity));
        this.commitSelectionChange(source);
        return true;
    }

    /**
     * 展开设计树零件/实体节点并替换测量选中集。
     *
     * @param descriptor 设计树节点测量展开描述符。
     * @returns 是否至少成功选中一个可测量实体。
     */
    public replaceSelectedEntitiesFromTreePart(
        descriptor: MeasureTreePartSelectionDescriptor,
    ): boolean {
        if (!this.isActive) {
            return false;
        }

        const entities = this.partMeasurableGeometryExpander.expand(this.viewer, descriptor);
        if (entities.length <= 0) {
            return false;
        }

        return this.replaceSelectedEntities(entities, 'feature-tree-part');
    }

    /**
     * 应用设计树节点轻量测量选择。
     *
     * 设计说明：
     * 1. 零件/装配体节点只需要节点级高亮与原点坐标，不应展开 BRep 面/边/点；
     * 2. 这里直接解析可渲染对象并写入测量视觉叠加层，避免创建 imported topology proxy；
     * 3. 同步清空普通测量实体集，防止旧的双选距离或活动锚点残留。
     *
     * @param descriptor 设计树节点轻量选择描述符。
     * @returns 是否成功解析宿主节点。
     */
    public applyTreeNodeVisualSelection(
        descriptor: MeasureTreeNodeVisualSelectionDescriptor,
    ): boolean {
        if (!this.isActive) {
            return false;
        }

        const hostObject = this.viewer.findSceneObjectById(descriptor.objectId);
        if (!hostObject) {
            return false;
        }

        this.viewer.scene.updateMatrixWorld(true);
        hostObject.updateMatrixWorld(true);

        const visualObjects = this.resolveTreeNodeVisualObjects(hostObject, descriptor);
        this.selectedEntities = [];
        this.pickHistory = [];
        this.hasTreeNodeVisualSelection = true;
        this.viewer.interactionStateManager?.setMeasureSelectedObjects(visualObjects);
        this.syncDatumSlotVisuals(null);
        this.calloutManager.syncTreeNodeOrigin({
            objectId: hostObject.uuid,
            displayName: descriptor.displayName?.trim() || hostObject.name?.trim() || '未命名节点',
            entityKindLabel: this.resolveTreeNodeVisualKindLabel(descriptor),
            originWorld: new THREE.Vector3().setFromMatrixPosition(hostObject.matrixWorld),
        });
        return true;
    }

    /**
     * 应用 GLB datum-slot 无边界测量选中。
     *
     * @param descriptor datum-slot 描述符。
     * @returns 是否成功写入测量选中集。
     */
    public applyUnboundedGlbDatumSlotSelection(
        descriptor: GlbDatumSlotTargetDescriptor,
    ): boolean {
        if (!this.isActive) {
            return false;
        }

        const resolved = this.unboundedGeometryResolver.resolveGlbDatumSlot(descriptor);
        if (!resolved) {
            return false;
        }

        const entityRef = this.unboundedGeometryResolver.toMeasureEntityRef(
            resolved.binding,
            resolved.worldPickPoint,
        );
        return this.applyUnboundedEntityRef(entityRef, 'feature-tree-datum-slot');
    }

    /**
     * 应用无边界测量实体引用到选中集。
     *
     * @param entityRef 无边界测量实体引用。
     * @param source 变更来源。
     * @returns 是否成功写入。
     */
    public applyUnboundedEntityRef(
        entityRef: MeasureEntityRef,
        source: MeasureSelectionUpdateSource = 'programmatic',
    ): boolean {
        if (!this.isActive || !entityRef.unboundedBinding) {
            return false;
        }

        this.pickHistory.push(cloneMeasureEntityRef(entityRef));
        this.selectedEntities = this.pickSelectionPolicy.resolveNextSelection(
            this.selectedEntities,
            entityRef,
            this.referencePolicy,
        );
        this.pickHistory = this.pickSelectionPolicy.prunePickHistory(
            this.pickHistory,
            this.selectedEntities,
        );
        this.commitSelectionChange(source);
        return true;
    }

    /**
     * 解析测量退出时应写回底层命令选中的场景对象。
     *
     * 设计说明：
     * 1. 必须在 `isActive=true` 阶段解析，确保测量保留的 imported 拓扑代理尚未被回收；
     * 2. 优先使用场景对象引用写回，避免退出清理后仅凭 UUID 二次查找失败。
     *
     * @returns 可写回底层选择中心的场景对象列表。
     */
    private resolveSelectedObjectsForWriteback(): THREE.Object3D[] {
        const uniqueObjects = new Map<string, THREE.Object3D>();

        this.selectedEntities.forEach((entity) => {
            const sceneObject = this.viewer.findSceneObjectById(entity.objectId);
            if (!sceneObject || uniqueObjects.has(sceneObject.uuid)) {
                return;
            }
            uniqueObjects.set(sceneObject.uuid, sceneObject);
        });

        return Array.from(uniqueObjects.values());
    }

    /**
     * 清空当前测量选中集（会话保持激活）。
     *
     * 设计说明：
     * 1. 供空白点击与 Esc 等交互统一收口；
     * 2. 不重置 referencePolicy，保留工具栏测量策略；
     * 3. 同步测量选中视觉与 callout / 双选测量线。
     */
    public clearSelectedEntities(): void {
        if (!this.isActive) {
            return;
        }
        if (
            this.selectedEntities.length <= 0
            && this.pickHistory.length <= 0
            && !this.hasTreeNodeVisualSelection
        ) {
            return;
        }

        this.selectedEntities = [];
        this.pickHistory = [];
        this.hasTreeNodeVisualSelection = false;
        this.commitSelectionChange('clear');
    }

    /**
     * 处理测量模式下的点击拾取。
     *
     * 设计说明：
     * 1. 首次点击加入测量选中集；
     * 2. 重复点击同一对象时取消选中（toggle）；
     * 3. 点到点模式双选已满时，保留先选并滑动替换后选；
     * 4. 视觉同步走独立测量选中通道，避免污染底层命令选中集。
     *
     * @param pick 射线命中结果。
     * @returns 是否成功处理。
     */
    public handlePick(pick: THREE.Intersection<THREE.Object3D>): boolean {
        if (!this.isActive) {
            return false;
        }

        let entityRef: MeasureEntityRef | null = null;

        const topologyPick = resolveTopologySelectionPick(pick.object);
        const brepGeometryDescriptor = resolveBRepGeometryDescriptor(pick.object);
        if (topologyPick && brepGeometryDescriptor) {
            entityRef = resolveMeasureEntityRef(pick);
        } else if (isMeasurableObject(pick.object)) {
            const formalUnbounded = this.unboundedGeometryResolver.resolveFormalDatumSceneObject(pick.object);
            if (formalUnbounded) {
                entityRef = this.unboundedGeometryResolver.toMeasureEntityRef(
                    formalUnbounded.binding,
                    pick.point.clone(),
                );
            } else {
                entityRef = resolveMeasureEntityRef(pick);
            }
        }

        if (!entityRef) {
            return false;
        }

        this.pickHistory.push(cloneMeasureEntityRef(entityRef));

        this.selectedEntities = this.pickSelectionPolicy.resolveNextSelection(
            this.selectedEntities,
            entityRef,
            this.referencePolicy,
        );
        this.pickHistory = this.pickSelectionPolicy.prunePickHistory(
            this.pickHistory,
            this.selectedEntities,
        );

        this.commitSelectionChange('viewport-pick');
        return true;
    }

    /**
     * 提交测量选中集变更并同步视觉、标注与事件。
     *
     * @param source 变更来源。
     */
    private commitSelectionChange(source: MeasureSelectionUpdateSource): void {
        this.hasTreeNodeVisualSelection = false;
        this.syncSelectionVisuals();
        this.syncDatumSlotVisuals(this.resolveActiveDatumSlotDescriptor());
        this.syncCalloutForSelection();
        this.publishSelectionUpdated(source);
    }

    /**
     * 发布测量选中集变更事件。
     *
     * @param source 变更来源。
     */
    private publishSelectionUpdated(source: MeasureSelectionUpdateSource): void {
        this.selectionUpdatedSubject.next({
            selectedEntities: this.getSelectedEntities(),
            activeAnchor: this.resolveActiveAnchorEntity(),
            source,
            timestamp: Date.now(),
        });
    }

    /**
     * 按当前选中集同步测量标注。
     *
     * 设计说明：单选锚点通道与双选测量通道并行，互不清除；
     * 测量策略变更仅影响双选通道，不得影响活动锚点浮窗。
     */
    private syncCalloutForSelection(): void {
        this.calloutManager.syncActiveAnchor(
            this.resolveActiveAnchorEntity(),
            this.referencePolicy,
        );

        if (this.selectedEntities.length === 2) {
            this.calloutManager.syncPairMeasurement(
                this.selectedEntities[0]!,
                this.selectedEntities[1]!,
                this.referencePolicy,
                this.pickHistory,
            );
            return;
        }

        this.calloutManager.clearPairMeasurement();
    }

    /**
     * 解析当前应显示的测量锚点实体。
     *
     * 规则：从最近一次点击向前回溯，返回第一个仍存在于选中集内的实体引用。
     *
     * @returns 活动锚点实体；选中集为空时返回 null。
     */
    private resolveActiveAnchorEntity(): MeasureEntityRef | null {
        if (this.selectedEntities.length <= 0) {
            return null;
        }

        const selectedIdSet = new Set(
            this.selectedEntities.map((entity) => entity.objectId),
        );

        for (let index = this.pickHistory.length - 1; index >= 0; index -= 1) {
            const historyEntry = this.pickHistory[index];
            if (!selectedIdSet.has(historyEntry.objectId)) {
                continue;
            }

            const matchedEntity = this.selectedEntities.find(
                (entity) => entity.objectId === historyEntry.objectId,
            );
            if (matchedEntity) {
                return cloneMeasureEntityRef(matchedEntity);
            }

            return cloneMeasureEntityRef(historyEntry);
        }

        return null;
    }

    /**
     * 解析当前测量选中集应显示的 datum-slot 描述符。
     *
     * 说明：datum-slot overlay 当前仅支持单槽位渲染，优先取活动锚点上的槽位。
     *
     * @returns datum-slot 描述符；无选中槽位时返回 null。
     */
    private resolveActiveDatumSlotDescriptor(): GlbDatumSlotTargetDescriptor | null {
        const activeAnchor = this.resolveActiveAnchorEntity();
        if (activeAnchor?.unboundedBinding?.datumSlot) {
            return activeAnchor.unboundedBinding.datumSlot;
        }

        for (let index = this.selectedEntities.length - 1; index >= 0; index -= 1) {
            const datumSlot = this.selectedEntities[index]?.unboundedBinding?.datumSlot;
            if (datumSlot) {
                return datumSlot;
            }
        }

        return null;
    }

    /**
     * 同步 datum-slot 测量选中 overlay。
     *
     * @param descriptor 目标 datum-slot 描述符；为空时清空 overlay。
     */
    private syncDatumSlotVisuals(descriptor: GlbDatumSlotTargetDescriptor | null): void {
        this.viewer.setMeasureDatumSlotSelected?.(descriptor);
    }

    /**
     * 解析设计树节点轻量高亮对象。
     *
     * @param hostObject 设计树节点宿主对象。
     * @param descriptor 设计树节点轻量选择描述符。
     * @returns 去重后的可渲染对象列表。
     */
    private resolveTreeNodeVisualObjects(
        hostObject: THREE.Object3D,
        descriptor: MeasureTreeNodeVisualSelectionDescriptor,
    ): THREE.Object3D[] {
        const resolvedObjects: THREE.Object3D[] = [];
        const seenObjectIds = new Set<string>();

        const pushObject = (object: THREE.Object3D): void => {
            if (seenObjectIds.has(object.uuid)) {
                return;
            }
            seenObjectIds.add(object.uuid);
            resolvedObjects.push(object);
        };

        if (Array.isArray(descriptor.highlightTargets) && descriptor.highlightTargets.length > 0) {
            descriptor.highlightTargets.forEach((highlightTarget) => {
                const matchedObject = this.findObjectByIdInSubtree(hostObject, highlightTarget.objectId)
                    ?? this.viewer.findSceneObjectById(highlightTarget.objectId);
                if (!matchedObject) {
                    return;
                }

                expandImportedModelRenderableTargets(
                    matchedObject,
                    highlightTarget.primitiveIndices,
                ).forEach((target) => pushObject(target.object));
            });
        }

        if (resolvedObjects.length > 0) {
            return resolvedObjects;
        }

        this.importedTreeNodeTargetResolver.resolve(hostObject).forEach((target) => {
            pushObject(target.object);
        });
        return resolvedObjects;
    }

    /**
     * 在宿主子树内按对象 ID 查找对象。
     *
     * @param root 查找根对象。
     * @param objectId 目标对象 ID。
     * @returns 命中的对象；未命中时返回 null。
     */
    private findObjectByIdInSubtree(root: THREE.Object3D, objectId: string): THREE.Object3D | null {
        if (root.uuid === objectId) {
            return root;
        }
        return root.getObjectByProperty('uuid', objectId) ?? null;
    }

    /**
     * 解析设计树节点轻量测量浮窗类型标签。
     *
     * @param descriptor 设计树节点轻量选择描述符。
     * @returns 浮窗类型标签。
     */
    private resolveTreeNodeVisualKindLabel(
        descriptor: MeasureTreeNodeVisualSelectionDescriptor,
    ): string {
        if (descriptor.glbNodeRole === 'assembly') {
            return '装配体原点';
        }
        if (descriptor.glbNodeRole === 'part' || descriptor.glbNodeRole === 'component') {
            return '零部件原点';
        }
        if (descriptor.glbNodeRole === 'body') {
            return '实体原点';
        }
        return '节点原点';
    }

    /**
     * 将当前测量选中集同步到交互视觉叠加层。
     */
    public syncSelectionVisuals(): void {
        const selectedObjects = this.selectedEntities
            .map((entity) => this.viewer.findSceneObjectById(entity.objectId))
            .filter((object): object is THREE.Object3D => object !== null);

        this.viewer.interactionStateManager?.setMeasureSelectedObjects(selectedObjects);
        // 测量选中视觉属于普通场景脏状态，不需要同步重入渲染管线。
        this.viewer.requestRender?.();
    }

    /**
     * 解析测量会话的文档上下文。
     *
     * @param mode 当前 Viewer 模式。
     * @returns 文档上下文。
     */
    private resolveDocumentContext(mode: ViewerMode): MeasureDocumentContext {
        if (
            mode === ViewerMode.Assembly
            || mode === ViewerMode.MoveComponent
            || mode === ViewerMode.LinearComponentPattern
        ) {
            return 'assembly';
        }
        return 'part';
    }

    /**
     * 克隆双选参考点策略，避免引用共享导致后续策略污染。
     *
     * @param policy 原始策略快照。
     * @returns 克隆后的策略对象。
     */
    private cloneReferencePolicy(policy: MeasureReferencePolicy): MeasureReferencePolicy {
        return {
            pairMode: policy.pairMode,
            arcCircleMode: policy.arcCircleMode,
        };
    }
}
